import {
  AttachFileOutlined,
  ChevronRightRounded,
  InfoOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  Checkbox,
  Divider,
  Tooltip,
  Typography,
} from "@mui/material";
import { blue, green, grey, red, yellow } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isFunction, isNil, size } from "lodash";
import { memo, useCallback, useMemo } from "react";
import { htmlStringToText } from "../../_src_shared/utils/functions/htmlStringToText";
import CommentsRenderer from "../renderers/CommentsRenderer";
import Flx from "../layout/Flx";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import CellRendererRequirementType from "../agGrid/cellRenderer/CellRendererRequirementType";
import { useSelector } from "react-redux";
import ColumnDefHeaderLabelWithIcon from "../../_src_shared/agGrid/columnDef/headerComponent/ColumnDefHeaderLabelWithIcon";
import CommentThreadPreviewTooltip from "../tooltips/CommentThreadPreviewTooltip";
import { getDaysAgo } from "../../_src_shared/utils/functions/getDaysAgo";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import Txt from "../typography/Txt";
import { formatDateShort } from "../../utils/dates/formatDateShort";
import HtmlEditor from "../html/HtmlEditor";

const LoanUnderwritingConditionsTable = ({
  quickFilter,
  rowData,
  onRowClicked,
}) => {
  const hasDocStipStatus = useMemo(() => {
    return rowData?.some((row) => !isNil(row?.docStipStatus));
  }, [rowData]);
  const hasSecondLevelReview = useMemo(() => {
    return rowData?.some((row) => row?.status === "Second Level Review");
  }, [rowData]);
  const hasResolution = useMemo(() => {
    return rowData?.some((row) => row?.resolutionNotes || row?.resolvedBy);
  }, [rowData]);
  const condition = useSelector((state) => state?.sidebar?.state);
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Type",
        field: "requirementType",
        width: 120,
        // cellClass: "ag-cell-vertical-center",
        tooltipField: "requirementType",
        tooltipValueGetter: (p) => {
          return p?.data?.requirementType;
        },
        cellStyle: { cursor: "pointer" },
        cellRenderer: (params) => (
          <CellRendererRequirementType value={params.value} />
        ),
      },
      {
        headerName: "Status",
        field: "status", // ['Open', 'In Progress',  'Accepted', 'Rejected', 'Second Level Review']
        width: hasSecondLevelReview ? 140 : 100,
        sort: "desc",
        cellStyle: { cursor: "pointer" },
        // cellClass: "ag-cell-vertical-center",
      },

      {
        headerName: "Document Status",
        field: "docStipStatus", // ["Open Item", "Received - Pending Review", "Reviewed - Not Accepted", "Accepted"]
        width: 180,
        // sort: "desc",
        hide: !hasDocStipStatus,
        // cellClass: "ag-cell-vertical-center",
        tooltipComponent: null,
        valueFormatter: (p) => {
          const status = p.value;

          if (isNil(status)) {
            return "N/A";
          }
          return status;
        },
        cellStyle: (params) => {
          const status = params.value;
          // let backgroundColor = green[100];
          let backgroundColor = "transparent";

          if (status === "Received - Pending Review") {
            backgroundColor = yellow[50];
          }
          if (status === "Reviewed - Not Accepted") {
            backgroundColor = red[50];
          }
          if (status === "Accepted") {
            backgroundColor = green[50];
          }
          return {
            cursor: "pointer",
            // opacity: isNil(status) ? 0.65 : 1,

            fontStyle:
              isNil(status) || status === "Open Item" ? "normal" : "italic",
            backgroundColor,
          };
        },
      },

      {
        headerName: "Requirement Details",
        field: "htmlString",
        minWidth: 250,
        flex: 1,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          lineHeight: 1.5,
          cursor: "pointer",
          paddingTop: 10,
          paddingBottom: 10,
          wrapText: true, // Wrap Text
          whiteSpace: "normal", // Allow normal white space handling
          autoHeight: true, // Adjust Cell Height to Fit Wrapped Text
        },
        valueFormatter: (p) => {
          if (isNil(p.value) || p.value === "") {
            return "N/A";
          }

          return htmlStringToText(p.value);
        },
        cellRenderer: (p) => {
          if (isNil(p.value) || p.value === "") {
            return "N/A";
          }
          if (p?.data?.docType) {
            return (
              <Flx column>
                <Txt bold fontSize={12}>
                  Missing Document: {p?.data?.docType}
                </Txt>
                <Txt sx={{ fontSize: "12px" }}>
                  <HtmlEditor
                    value={p?.value} // Pass initial value only
                    suppressOuterBorder
                    readOnly
                  />
                </Txt>
                {/* <Txt sx={{ fontSize: "12px" }}>{htmlStringToText(p.value)}</Txt> */}
              </Flx>
            );
          }

          return (
            <HtmlEditor
              value={p?.value} // Pass initial value only
              suppressOuterBorder
              readOnly
            />
          );
          // return htmlStringToText(p.value);
        },
      },

      {
        headerName: "Updated",
        field: "updateDateTime",
        // sort: "desc",
        width: 110,
        // cellClass: "ag-cell-vertical-center",
        tooltipComponent: (params) => (
          <Flx
            ac
            g={1}
            sx={{
              background: "#ffffff",
              border: `1px solid ${grey[300]}`,
              p: 1,
              borderRadius: 2,
            }}
          >
            Last Updated <b>{formatDateTime(params.data?.updateDateTime)}</b> (
            {getDaysAgo(params.data?.updateDateTime) === 0
              ? `Today ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
              : getDaysAgo(params.data?.updateDateTime) === 1
                ? `1 day ago ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
                : `${getDaysAgo(params.data?.updateDateTime)} days ago ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`}
            )
          </Flx>
        ),
        tooltipValueGetter: (params) => {
          // return params.data?.updateDateTime;
          return `Last Updated: ${formatDateTime(params.data?.updateDateTime)} (${
            getDaysAgo(params.data?.updateDateTime) === 0
              ? `Today ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
              : getDaysAgo(params.data?.updateDateTime) === 1
                ? `1 day ago ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
                : `${getDaysAgo(params.data?.updateDateTime)} days ago ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
          })`;
        },
        // cellStyle: {
        //   // lineHeight: 1.5,
        //   cursor: "pointer",
        //   // paddingTop: 10,
        //   // paddingBottom: 10,
        //   // display: "flex",
        //   // alignItems: "center",
        //   // wrapText: true, // Wrap Text
        //   // whiteSpace: "normal", // Allow normal white space handling
        //   // autoHeight: true, // Adjust Cell Height to Fit Wrapped Text
        // },
        cellRenderer: (p) => {
          const daysAgo = getDaysAgo(p?.value);
          let text = "";
          if (daysAgo === 0) {
            text = "Today";
          } else if (daysAgo === 1) {
            text = `1 day ago`;
          } else {
            text = `${daysAgo} days ago`;
          }
          // if (daysAgo === 0) {
          //   text = "Today";
          // } else if (daysAgo === 1) {
          //   text = `1 day ago ${isNil(p.data?.updatedBy) ? "" : `by ${p.data?.updatedBy}`}`;
          // } else {
          //   text = `${daysAgo} days ago ${isNil(p.data?.updatedBy) ? "" : `by ${p.data?.updatedBy}`}`;
          // }

          if (daysAgo > 60) {
            text = formatDateShort(p?.value);
          }

          return (
            <Flx g={0.4} ac>
              <span>{text}</span>
              <InfoOutlined
                className="thin-9"
                sx={{ color: "inherit", fontSize: "16px" }}
              />
            </Flx>
          );

          return (
            <Flx ac g={0.4}>
              {text}
              <InfoOutlined
                className="thin-9"
                sx={{ color: "inherit", fontSize: "16px" }}
              />
            </Flx>
          );
        },
      },
      {
        width: 130,
        headerName: "Interactions",
        field: "interactions",
        // cellClass: "ag-cell-vertical-center",
        cellStyle: {
          cursor: "pointer",
          // background: "red",
          // padding: 0,
          paddingTop: 13,
          paddingLeft: 26,
          // display: "flex",
          // justifyContent: "space-evenly",
          // alignItems: "center",
        },
        // pinned: isNil(condition) ? null : "right",
        valueGetter: (p) => {
          const documentIds = p.data?.uploadedDocumentIds || [];
          return size(documentIds);
        },
        // tooltipValueGetter: (p) => {
        //   if (size(p?.data?.uploadedDocumentIds) === 0) {
        //     return "No documents currently associated with this condition.";
        //   }
        //   if (size(p?.data?.uploadedDocumentIds) === 1) {
        //     return "(1) condition document .";
        //   }
        //   return `(${size(p?.data?.uploadedDocumentIds)}) condition documents`;
        // },
        cellRenderer: (p) => {
          return (
            <Flx g={2.5}>
              <CommentThreadPreviewTooltip
                comments={p.data?.comments || p.data?.conditionComments}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CommentsRenderer
                    onlyCount
                    comments={p.data?.comments || p.data?.conditionComments}
                  />
                </Box>
              </CommentThreadPreviewTooltip>

              <Tooltip title={getDocumentCountTooltipText(p)}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DocumentIdsCountBadge
                    count={size(p?.data?.uploadedDocumentIds)}
                  />
                </Box>
              </Tooltip>
            </Flx>
          );
          return (
            <Flx
              fw
              ac
              g={2}
              center
              sx={{
                // justifyContent: "space-between",
                // justifyContent: "space-evenly",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                pr: 0,
                // paddingLeft: "18px",
                // paddingRight: "12px",
                // px: 1,
                flexGrow: 1,
              }}
            >
              <CommentThreadPreviewTooltip
                comments={p.data?.comments || p.data?.conditionComments}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CommentsRenderer
                    onlyCount
                    comments={p.data?.comments || p.data?.conditionComments}
                  />
                </Box>
              </CommentThreadPreviewTooltip>

              <Tooltip title={getDocumentCountTooltipText(p)}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DocumentIdsCountBadge
                    count={size(p?.data?.uploadedDocumentIds)}
                  />
                </Box>
              </Tooltip>

              {/* <ChevronRightRounded sx={{ color: grey[600] }} /> */}
            </Flx>
          );
        },
      },
      {
        width: 100,
        // cellClass: "ag-cell-vertical-center",
        field: "displayExternally",
        headerComponent: ColumnDefHeaderLabelWithIcon,
        headerTooltip:
          "Resolved: Indicates whether this condition was resolved.",
        tooltipValueGetter: (params) => {
          return params.data;
        },
        tooltipComponent: (p) => {
          const notes = p?.data?.resolutionNotes;
          const resolvedBy = p?.data?.resolvedBy;
          return (
            <Card sx={{ p: 1.5, maxWidth: "400px" }}>
              <Flx column g={1}>
                <Txt>
                  Resolved By: <b>{resolvedBy || "Unknown"}</b>
                </Txt>
                <Divider />
                <Flx g={0.5}>
                  <Txt>Notes:</Txt>
                  <Txt bold> {notes}</Txt>
                </Flx>
              </Flx>
            </Card>
          );
        },
        headerName: "Resolved",
        cellStyle: {
          cursor: "pointer",
        },
        hide: !hasResolution,
        valueGetter: (p) => {
          if (
            isNil(p?.data?.displayExternally) ||
            !p?.data?.displayExternally
          ) {
            return false;
          } else {
            return true;
          }
        },
        cellRenderer: (p) => {
          const checked = p?.data?.resolutionNotes || p?.data?.resolvedBy;
          return <Checkbox checked={checked} disabled />;
          return (
            <Flx center sx={{ height: "100%" }}>
              <Checkbox checked={checked} disabled />
            </Flx>
          );
        },
      },
      {
        width: 100,
        // cellClass: "ag-cell-vertical-center",
        field: "displayExternally",
        headerComponent: ColumnDefHeaderLabelWithIcon,
        headerTooltip:
          "External: Indicates whether this condition is displayed externally with the counterparty.",
        tooltipValueGetter: (p) => {
          if (p?.data?.displayExternally) {
            return "This condition is displayed externally with the counterparty.";
          } else {
            return "This is an INTERNAL only condition that can not be seen and is not displayed externally with a loan counterparty.";
          }
        },
        headerName: "External",
        cellStyle: {
          cursor: "pointer",
        },
        valueGetter: (p) => {
          if (
            isNil(p?.data?.displayExternally) ||
            !p?.data?.displayExternally
          ) {
            return false;
          } else {
            return true;
          }
        },
        cellRenderer: (p) => {
          return <Checkbox checked={p.value} disabled />;
          return (
            <Flx center sx={{ height: "100%" }}>
              <Checkbox checked={p.value} disabled />
            </Flx>
          );
        },
      },
      {
        width: 70,
        // cellClass: "ag-cell-vertical-center",
        headerName: "Auto",
        headerComponent: ColumnDefHeaderLabelWithIcon,
        headerComponentParams: { menuIcon: "fa-cog" },
        headerTooltip:
          "Auto: Indicates whether this condition was auto-generated by the system or manually added by a user.",
        tooltipValueGetter: (p) => {
          if (p?.data?.autoGenerated) {
            return "This condition was auto-generated by the system.";
          } else {
            return `This condition was manually added by ${p?.data?.createdBy}`;
          }
        },
        field: "autoGenerated",
        cellStyle: {
          cursor: "pointer",
        },
        valueGetter: (p) => {
          if (isNil(p?.data?.autoGenerated) || !p?.data?.autoGenerated) {
            return false;
          } else {
            return true;
          }
        },
        cellRenderer: (p) => {
          return <Checkbox checked={p.value} disabled />;
          return (
            <Flx center sx={{ height: "100%" }}>
              <Checkbox checked={p.value} disabled />
            </Flx>
          );
        },
      },
    ],
    [condition, hasDocStipStatus, hasSecondLevelReview, hasResolution],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const getRowStyle = useCallback(
    (params) => {
      const isActive = condition?._id === params?.data?._id;
      // if (condition?._id === params?.data?._id) {
      //   return {
      //     // backgroundColor: "#e3f2fd63",
      //     fontWeight: 600,
      //     borderLeft: `4px solid ${blue[500]}`,
      //   };
      // }

      const status = params.data?.status;
      const docStatus = params.data?.docStipStatus;

      let backgroundColor = "transparent";
      let borderColor = "transparent";

      if (status === "Rejected" || docStatus === "Reviewed - Not Accepted") {
        // RED
        backgroundColor = "#ffebeea6";
        borderColor = red[100];
      }

      if (
        status === "Second Level Review" ||
        status === "In Progress" ||
        docStatus === "Received - Pending Review"
      ) {
        // YELLOW
        backgroundColor = "#fffde7a6";
        borderColor = yellow[100];
      }

      if (status === "Accepted") {
        backgroundColor = "#e8f5e9a6";
        borderColor = green[100];
      }
      return {
        backgroundColor,
        fontWeight: isActive ? 600 : 400,
        borderLeft: isActive ? `4px solid #2962ff` : "none",
        borderRight: isActive ? `4px solid #2962ff` : "none",
        borderTop: `1px solid ${borderColor}`,
      };
      return {
        backgroundColor: "#fffde7a6",
        // backgroundColor: yellow[50],
        borderTop: `1px solid #ffffff`,
        borderBottom: `1px solid #ffffff`,
      };
    },
    [condition],
  );

  const defaultColDef = useMemo(
    () => ({
      cursor: "pointer",
      // tooltipComponent: () => (
      //   <Flx
      //     ac
      //     g={1}
      //     sx={{
      //       background: "#ffffff",
      //       border: `1px solid ${grey[300]}`,
      //       p: 1,
      //       borderRadius: 2,
      //     }}
      //   >
      //     Click row to address Loan Condition
      //     <ChevronRightRounded sx={{ color: grey[600] }} />
      //   </Flx>
      // ),
      // tooltipValueGetter: () => "Click to address condition",
    }),
    [],
  );

  const handleOnRowClicked = useCallback(
    (params) => {
      onRowClicked?.(params?.data);
    },
    [onRowClicked],
  );
  return (
    <Box
      sx={{
        // maxHeight: "800px",
        overflowY: "auto",
        // border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        ".ag-row-last": {
          borderBottom: "none !important",
        },
        ".ag-header-viewport": {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <AgGridTableWrapper
        height="100%"
        // simpleTable
        compactTable
        suppressMinHeightWhenPopulated={rowData}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          quickFilterText={quickFilter}
          getRowId={getRowId}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          onRowClicked={handleOnRowClicked}
          domLayout="autoHeight"
          tooltipShowDelay={100}
          enableCellTextSelection
          //   tooltipMouseTrack
        />
      </AgGridTableWrapper>
    </Box>
  );
};

const DocumentIdsCountBadge = memo(({ count, onClick }) => {
  return (
    <Tooltip
      title={
        isFunction(onClick)
          ? `${count} ${count === 1 ? "comment" : "comments"} — click to expand`
          : undefined
      }
    >
      <Box
        onClick={onClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.1,
          cursor: onClick ? "pointer" : "default",
          color: count > 0 ? "primary.main" : "text.secondary",
          "&:hover": onClick ? { color: "primary.dark" } : undefined,
        }}
      >
        <AttachFileOutlined sx={{ fontSize: 13 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
          {count}
        </Typography>
      </Box>
    </Tooltip>
  );
});

const getCommentCountTooltipText = (p) => {
  if (size(p.data?.comments || p?.data?.conditionComments) === 0) {
    return "No comments currently associated with this condition.";
  }
  if (size(p.data?.comments || p?.data?.conditionComments) === 1) {
    return "(1) comment currently associated with this condition.";
  }
  return `(${size(p.data?.comments || p?.data?.conditionComments)}) comments currently associated with this condition. Click to view.`;
};
const getDocumentCountTooltipText = (p) => {
  if (size(p?.data?.uploadedDocumentIds) === 0) {
    return "No documents currently associated with this condition.";
  }
  if (size(p?.data?.uploadedDocumentIds) === 1) {
    return "(1) condition document .";
  }
  return `(${size(p?.data?.uploadedDocumentIds)}) condition documents`;
};
export default LoanUnderwritingConditionsTable;
